import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import Uploadimg from "../../assets/img/uploaddocoment.png";
import {
  getIdentityDetails,
  getUserDisplayName,
  haveValue,
  toast,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import Cancelimg from "../../assets/img/cancel-01.svg";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import * as pdfjsLib from "pdfjs-dist";
import AuthContext from "../../context/auth/AuthContext";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import provincesData from "../../Utils/provincia_municipios.json";
import streetTypeCodes from "../../Utils/streetTypeCodes.json";
import InsuranceReviewModal from "./InsuranceReviewModal";
import { OutlineShieldIcon, DownloadIcon } from "../../helper/svgHelper";
import moreInfoimg from "../../assets/img/moreInfoimg.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";
import DatePicker from "react-datepicker";
import constants from "../../Utils/constants";
import {
  FileInput,
  DatePickerHeader,
  Button,
  Icon,
  Notice,
  Select as FormSelect,
  Skeleton,
  Tabs,
  TextField,
} from "../design";
import "./purchaseInsurance.css";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];
const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const INSURANCE_COVERED_OPTIONS = [
  { title: "myself", icon: "user", key: "myself" },
  {
    title: "someoneElse",
    icon: "users",
    key: "someoneElse",
  },
];

const PERSON_TYPE_OPTIONS = [
  { title: "individual", icon: "user", key: "individual" },
  { title: "company", icon: "building", key: "company" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { label: "selectDocumentType", value: "" },
  { label: "dni", value: "dni" },
  { label: "nie", value: "nie" },
  { label: "passport", value: "passport" },
];

// Fields shown when insurance_covered === "myself"
const FIELDS_MYSELF = [
  {
    label: "documentType",
    type: "select",
    options: DOCUMENT_TYPE_OPTIONS,
    name: "document_type",
  },
  {
    label: "documentNumber",
    placeholder: "enterDocumentNumber",
    name: "document_number",
  },
  { label: "firstName", placeholder: "enterFirstName", name: "first_name" },
  { label: "lastName", placeholder: "enterLastName", name: "last_name" },
  {
    label: "dateofbirth",
    type: "date",
    name: "date_of_birth",
    class: "full-width",
  },
];

// Fields shown when insurance_covered === "someoneElse" AND person_type === "individual"
const FIELDS_INDIVIDUAL = [
  {
    label: "documentType",
    type: "select",
    options: DOCUMENT_TYPE_OPTIONS,
    name: "document_type",
  },
  {
    label: "documentNumber",
    placeholder: "enterDocumentNumber",
    name: "document_number",
  },
  { label: "firstName", placeholder: "enterFirstName", name: "first_name" },
  { label: "lastName", placeholder: "enterLastName", name: "last_name" },
  {
    label: "dateofbirth",
    type: "date",
    name: "date_of_birth",
    class: "full-width",
  },
  { label: "email", placeholder: "enterEmail", name: "email", required: false },
  { label: "phoneNumber", name: "phone_number", required: false },
];

// Fields shown when insurance_covered === "someoneElse" AND person_type === "company"
// document_type is hidden (CIF is implicit); document_number relabelled; first_name → company_name
const FIELDS_COMPANY = [
  { label: "enterCif", placeholder: "enterCif", name: "document_number" },
  { label: "companyName", placeholder: "enterCompanyName", name: "first_name" },
  { label: "email", placeholder: "enterEmail", name: "email", required: false },
  { label: "phoneNumber", name: "phone_number", required: false },
];

const INITIAL_INSURANCE_DETAILS = {
  insurance_covered: "myself",
  gran_tenedor: false,
  lease_start_date: "",
  policy_start_date: "",
};

const createBeneficiaryId = () =>
  window.crypto?.randomUUID?.() ||
  `beneficiary-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const createBeneficiary = (overrides = {}) => ({
  id: createBeneficiaryId(),
  person_type: "individual",
  document_type: "",
  document_number: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  email: "",
  phone_number: "",
  phone_number_full: "",
  province: "",
  city: "",
  street_name: "",
  street_type: "",
  street_number: "",
  block: "",
  ladder: "",
  floor: "",
  door: "",
  postcode: "",
  additional_address: "",
  ...overrides,
});

// Maps the workspace-level saved beneficiary (Manage Workspace → Invitations →
// Edit beneficiary details) onto a fresh beneficiary card's fields.
const beneficiaryPrefillFromWorkspace = (saved) =>
  saved
    ? {
        person_type: saved.person_type || "individual",
        document_type: saved.document_type || "",
        document_number: saved.document_number || "",
        first_name: saved.first_name || "",
        last_name: saved.last_name || "",
        date_of_birth: saved.date_of_birth || "",
        province: saved.province || "",
        city: saved.city || "",
        street_name: saved.street_name || "",
        street_type: saved.street_type || "",
        street_number: saved.street_number || "",
        block: saved.block || "",
        ladder: saved.ladder || "",
        floor: saved.floor || "",
        door: saved.door || "",
        postcode: saved.postcode || "",
        additional_address: saved.additional_address || "",
      }
    : {};

const workspaceBeneficiaryPayload = (
  beneficiary = {},
  granTenedor = beneficiary.gran_tenedor
) => ({
  person_type: beneficiary.person_type || "individual",
  document_type: beneficiary.document_type || "",
  document_number: beneficiary.document_number || "",
  first_name: beneficiary.first_name || "",
  last_name: beneficiary.last_name || "",
  date_of_birth: beneficiary.date_of_birth || "",
  province: beneficiary.province || "",
  city: beneficiary.city || "",
  street_name: beneficiary.street_name || "",
  street_type: beneficiary.street_type || "",
  street_number: beneficiary.street_number || "",
  block: beneficiary.block || "",
  ladder: beneficiary.ladder || "",
  floor: beneficiary.floor || "",
  door: beneficiary.door || "",
  postcode: beneficiary.postcode || "",
  additional_address: beneficiary.additional_address || "",
  gran_tenedor: granTenedor === true,
});

const beneficiaryMatchesWorkspace = (beneficiary, saved, granTenedor) => {
  const current = workspaceBeneficiaryPayload(beneficiary, granTenedor);
  const snapshot = workspaceBeneficiaryPayload(saved);
  return Object.keys(current).every((key) => current[key] === snapshot[key]);
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatSize = (size) => {
  const kb = size / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

const isValidDateOfBirth = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    year >= 1900 &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date <= today
  );
};

const displayDateToDate = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return null;
  const [day, month, year] = value.split("/").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
};

const dateToDisplayDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const normalizeDocumentNumber = (value) =>
  String(value ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

const getSelectedTenantRecords = (selectedTenants = []) =>
  selectedTenants.flatMap((selection) => {
    const participants = selection?.application_id?.participants;
    if (participants?.length) {
      return participants
        .map((participant) => participant?.tenant_info)
        .filter(Boolean);
    }
    return [selection?.tenantid || selection?.tenant_info || selection].filter(
      Boolean
    );
  });

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────
const StyledDatePicker = ({
  value,
  onChange,
  minDate = new Date("1900-01-01"),
  maxDate,
  openToDate,
  hasError = false,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const normalizedMinDate = minDate ? new Date(minDate) : null;
  normalizedMinDate?.setHours(0, 0, 0, 0);
  const normalizedMaxDate = maxDate ? new Date(maxDate) : null;
  normalizedMaxDate?.setHours(23, 59, 59, 999);
  const firstSelectableYear = normalizedMinDate?.getFullYear() ?? 1900;
  const lastSelectableYear =
    normalizedMaxDate?.getFullYear() ?? currentYear + 100;
  const isTodaySelectable =
    (!normalizedMinDate || today >= normalizedMinDate) &&
    (!normalizedMaxDate || today <= normalizedMaxDate);

  const handleChange = (date) => {
    if (!date) {
      onChange("");
      return;
    }
    if (
      (normalizedMinDate && date < normalizedMinDate) ||
      (normalizedMaxDate && date > normalizedMaxDate)
    )
      return;
    onChange(dateToDisplayDate(date));
  };

  return (
    <div className="native-like-datepicker">
      <DatePicker
        selected={displayDateToDate(value)}
        open={isCalendarOpen}
        onInputClick={() => setIsCalendarOpen(true)}
        onClickOutside={() => setIsCalendarOpen(false)}
        onSelect={() => setIsCalendarOpen(false)}
        onChange={handleChange}
        onChangeRaw={(e) => {
          if (
            e.target.value &&
            (!e.nativeEvent.inputType ||
              !e.nativeEvent.inputType.startsWith("delete"))
          ) {
            let digits = e.target.value.replace(/\D/g, "").slice(0, 8);
            if (digits.length >= 5) {
              digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
            } else if (digits.length >= 3) {
              digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }
            e.target.value = digits;
          }
        }}
        dateFormat={["dd/MM/yyyy", "ddMMyyyy", "dd-MM-yyyy", "dd.MM.yyyy"]}
        minDate={normalizedMinDate}
        maxDate={normalizedMaxDate}
        placeholderText="dd/mm/yyyy"
        className={`date-input ${!value ? "empty" : ""} ${hasError ? "input-error" : ""}`}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        openToDate={
          displayDateToDate(value) ||
          openToDate ||
          normalizedMinDate ||
          new Date(currentYear - 25, 0, 1)
        }
        renderCustomHeader={({ date, changeYear, changeMonth }) => (
          <DatePickerHeader
            date={date}
            changeYear={changeYear}
            changeMonth={changeMonth}
            firstYear={firstSelectableYear}
            lastYear={lastSelectableYear}
          />
        )}
      >
        <div className="datepicker-footer">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange("");
              setIsCalendarOpen(false);
            }}
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!isTodaySelectable}
            onClick={() => {
              handleChange(today);
              setIsCalendarOpen(false);
            }}
          >
            Today
          </Button>
        </div>
      </DatePicker>
      <span
        className="calendar-icon cursor-pointer"
        onClick={() => setIsCalendarOpen((prev) => !prev)}
      >
        <img src="/images/calendar-icon.svg" alt="Calendar Icon" />
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────
// AddressField
// ─────────────────────────────────────────────
export const AddressField = ({
  _t,
  insuranceDetails,
  setInsuranceDetails,
  isExpanded,
  setIsExpanded,
  showErrors = false,
  errors = {},
}) => {
  const compactSelectStyles = (hasValue) => ({
    control: (base) => ({
      ...base,
      minHeight: "none",
      height: "var(--select-height, 28px)",
      border: "0",
      boxShadow: "none",
      fontSize: "var(--select-font-size, 0.972vw)",
      padding: "0",
      cursor: "pointer",
      backgroundColor: "transparent",
      borderRadius: "0",
      "&:hover": { border: "0" },
    }),
    valueContainer: (base) => ({
      ...base,
      height: "var(--select-height, 28px)",
      padding: "0",
      margin: "0",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      overflow: "hidden",
    }),
    singleValue: (base) => ({
      ...base,
      margin: 0,
      color: "var(--ink)",
      gridArea: "1/1/2/3",
    }),
    placeholder: (base) => ({
      ...base,
      margin: 0,
      color: "var(--text-muted)",
      gridArea: "1/1/2/3",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      gridArea: "1/1/2/3",
    }),
    indicatorsContainer: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInsuranceDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selected) => {
    setInsuranceDetails((prev) => ({
      ...prev,
      [name]: selected?.value || "",
      ...(name === "province" ? { city: "" } : {}),
    }));
  };

  const numericOnlyOnKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Enter",
    ];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  };

  const isAddressIncomplete =
    !insuranceDetails.province ||
    !insuranceDetails.city ||
    !insuranceDetails.street_name ||
    !insuranceDetails.street_type ||
    !insuranceDetails.street_number ||
    !insuranceDetails.postcode;

  if (!isExpanded) {
    return (
      <div className="main-form-group full-width">
        <div className="cursor-pointer" onClick={() => setIsExpanded(true)}>
          <TextField
            id="address_collapsed"
            label={
              <>
                {_t("address")} <span className="required-star">*</span>
              </>
            }
            error={
              showErrors &&
              isAddressIncomplete &&
              _t("completeAddressIsRequired")
            }
            value={
              insuranceDetails.street_name
                ? `${insuranceDetails.street_name}${
                    insuranceDetails.street_number
                      ? ", " + insuranceDetails.street_number
                      : ""
                  }`
                : _t("clickToEnterAddress")
            }
            readOnly
            trailing={
              <img
                src="/images/editGreen.svg"
                alt="edit"
                className="addr-edit-icon"
              />
            }
          />
        </div>
      </div>
    );
  }

  const cityOptions = insuranceDetails.province
    ? provincesData
        .find((p) => p.Provincia === insuranceDetails.province)
        ?.municipios.map((m) => ({ label: m, value: m })) || []
    : [];

  const streetTypeValue = insuranceDetails.street_type
    ? (() => {
        const foundKey = Object.keys(streetTypeCodes).find(
          (key) => streetTypeCodes[key] === insuranceDetails.street_type
        );
        return {
          label: foundKey
            ? foundKey.charAt(0).toUpperCase() + foundKey.slice(1)
            : insuranceDetails.street_type,
          value: insuranceDetails.street_type,
        };
      })()
    : null;

  return (
    <div className="main-form-group full-width">
      <h5 className="address-section-heading">{_t("address")}</h5>
      <div className="addr-expanded-card">
        <div className="form-wrapper mt-0">
          {/* Province */}
          <div className="main-form-group" id="custom-select-search">
            <div
              className={`form-group ${showErrors && errors.province ? "form-group-error" : ""}`}
            >
              <label>
                {_t("province")} <span className="required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                name="province"
                value={
                  insuranceDetails.province
                    ? {
                        label: insuranceDetails.province,
                        value: insuranceDetails.province,
                      }
                    : null
                }
                options={provincesData.map((p) => ({
                  label: p.Provincia,
                  value: p.Provincia,
                }))}
                placeholder={_t("selectProvince")}
                styles={compactSelectStyles(!!insuranceDetails.province)}
                onChange={(selected) =>
                  handleSelectChange("province", selected)
                }
                isSearchable
                required
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
            </div>
            {showErrors && errors.province && (
              <p className="error-message">{errors.province}</p>
            )}
          </div>

          {/* Municipality */}
          <div
            className={`main-form-group ${!insuranceDetails.province ? "" : ""}`}
            id="custom-select-search"
          >
            <div
              className={`form-group ${showErrors && errors.city ? "form-group-error" : ""}`}
            >
              <label>
                {_t("municipality")} <span className="required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                name="city"
                value={
                  insuranceDetails.city
                    ? {
                        label: insuranceDetails.city,
                        value: insuranceDetails.city,
                      }
                    : null
                }
                options={cityOptions}
                placeholder={
                  insuranceDetails.province
                    ? _t("selectMunicipality")
                    : _t("selectProvinceFirst")
                }
                onChange={(selected) => handleSelectChange("city", selected)}
                styles={compactSelectStyles(!!insuranceDetails.city)}
                isSearchable
                isDisabled={!insuranceDetails.province}
                required
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
            </div>
            {showErrors && errors.city && (
              <p className="error-message">{errors.city}</p>
            )}
          </div>

          {/* Street Type */}
          <div className="main-form-group" id="custom-select-search">
            <div
              className={`form-group ${showErrors && errors.street_type ? "form-group-error" : ""}`}
            >
              <label>
                {_t("typeOfRoad")} <span className="required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                name="street_type"
                value={streetTypeValue}
                options={Object.keys(streetTypeCodes).map((type) => ({
                  label: type.charAt(0).toUpperCase() + type.slice(1),
                  value: streetTypeCodes[type],
                }))}
                placeholder={_t("selectType")}
                onChange={(selected) =>
                  handleSelectChange("street_type", selected)
                }
                styles={compactSelectStyles(!!insuranceDetails.street_type)}
                isSearchable
                required
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
            </div>
            {showErrors && errors.street_type && (
              <p className="error-message">{errors.street_type}</p>
            )}
          </div>

          {/* Street Name */}
          <div className="main-form-group">
            <TextField
              id="street_name"
              label={
                <>
                  {_t("streetName")} <span className="required-star">*</span>
                </>
              }
              error={showErrors && errors.street_name}
              type="text"
              name="street_name"
              placeholder={_t("eGMontesa")}
              value={insuranceDetails.street_name}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Street Number */}
          <div className="main-form-group">
            <TextField
              id="street_number"
              label={
                <>
                  {_t("number")} <span className="required-star">*</span>
                </>
              }
              error={showErrors && errors.street_number}
              type="text"
              name="street_number"
              placeholder={_t("eG5")}
              value={insuranceDetails.street_number}
              onChange={handleInputChange}
              onKeyDown={numericOnlyOnKeyDown}
              required
            />
          </div>

          {/* Postal Code */}
          <div className="main-form-group">
            <TextField
              id="postcode"
              label={
                <>
                  {_t("postalCode")} <span className="required-star">*</span>
                </>
              }
              error={showErrors && errors.postcode}
              type="text"
              name="postcode"
              value={insuranceDetails.postcode}
              placeholder="28006"
              onChange={handleInputChange}
              onKeyDown={numericOnlyOnKeyDown}
              maxLength={5}
              required
            />
          </div>
        </div>

        {/* Block / Ladder / Floor / Door */}
        <div className="addr-grid-4">
          {[
            { label: "block", name: "block", numeric: false, required: false },
            {
              label: "ladder",
              name: "ladder",
              numeric: false,
              required: false,
            },
            {
              label: "floor",
              name: "floor",
              numeric: true,
              required: false,
              max: 40,
            },
            { label: "door", name: "door", numeric: false, required: false },
          ].map(
            ({
              label,
              name,
              numeric,
              required,
              max,
              maxLength,
              placeholder,
            }) => (
              <TextField
                key={name}
                id={name}
                label={
                  <>
                    {_t(label)}
                    {required && <span className="required-star"> *</span>}
                  </>
                }
                type="text"
                name={name}
                value={insuranceDetails[name]}
                placeholder={placeholder}
                onChange={handleInputChange}
                onKeyDown={numeric ? numericOnlyOnKeyDown : undefined}
                onInput={
                  max != null
                    ? (e) => {
                        let v = e.target.value.replace(/\D/g, "");
                        if (v !== "" && Number(v) > max) v = String(max);
                        e.target.value = v;
                      }
                    : undefined
                }
                maxLength={maxLength}
                error={required && !insuranceDetails[name]}
                required={required}
              />
            )
          )}
        </div>

        {/* Additional Address */}
        <div className="form-wrapper additional-address addr-additional-address">
          <div className="main-form-group full-width">
            <TextField
              id="additional_address"
              label={_t("additionalAddress")}
              type="text"
              name="additional_address"
              value={insuranceDetails.additional_address}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div
          className="addr-collapse-btn d-flex align-items-center justify-content-center cursor-pointer"
          onClick={() => setIsExpanded(false)}
        >
          <img
            src="/images/down-arrow-grey.svg"
            alt="collapse"
            className={`me-2 collapse-arrow ${isExpanded ? "rotate-180" : ""}`}
          />
          <span>{_t("collapse")}</span>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// BeneficiaryCard
// ─────────────────────────────────────────────
const BeneficiaryCard = ({
  beneficiary,
  index,
  totalCount,
  onUpdate,
  onDelete,
  insuranceCovered,
  isAddrExpanded,
  setIsAddrExpanded,
  hideCard,
  showErrors,
  errors = {},
  showSaveToWorkspace,
  savingToWorkspace,
  onSaveToWorkspace,
  _t,
}) => {
  const activeFields = (() => {
    if (insuranceCovered === "myself") return FIELDS_MYSELF;
    if (beneficiary.person_type === "company") return FIELDS_COMPANY;
    return FIELDS_INDIVIDUAL;
  })();

  // Wrap onUpdate so AddressField's functional-update pattern works
  const setBeneficiary = (updater) => onUpdate(updater);

  const mainFields = activeFields.filter(
    (f) => f.name !== "email" && f.name !== "phone_number"
  );
  const contactFields = activeFields.filter(
    (f) => f.name === "email" || f.name === "phone_number"
  );

  return (
    <div
      className={
        hideCard
          ? "d-none"
          : "purchase-insurance-form d-flex flex-column custom-gap-3"
      }
    >
      {/* Card header */}
      <div
        className={`beneficiary-card-header d-flex justify-content-between ${
          index === 0 && insuranceCovered === "someoneElse"
            ? "beneficiary-card-header--stacked"
            : "align-items-center"
        }`}
      >
        <h5 className="d-flex align-items-center custom-gap-2 mb-0">
          <img src="/images/user-icon.svg" alt="User" />
          {_t("beneficiaryInformation")}
          {totalCount > 1 ? ` ${index + 1}` : ""}
          <img
            data-tooltip-id={`beneficiary-info-tooltip-${beneficiary.id}`}
            src={moreInfoimg}
            alt="Vector Icon"
            className="vactorImg vactorImg1 default-check-img"
            style={{ filter: "none" }}
          />
          <ReactTooltip
            id={`beneficiary-info-tooltip-${beneficiary.id}`}
            noArrow
            className="react-tooltip-styling details-skor-tooltip"
            render={() => <p>{_t("insuranceBeneficiaryTooltip")}</p>}
          />
        </h5>

        <div
          className={`beneficiary-card-header-actions ${
            index === 0 && insuranceCovered === "someoneElse"
              ? "beneficiary-card-header-actions--stacked"
              : index === 0
                ? "beneficiary-card-header-actions--inline"
                : ""
          }`}
        >
          {(insuranceCovered === "someoneElse" || totalCount > 1) && (
            <div className="beneficiary-card-actions">
              {insuranceCovered === "someoneElse" && (
                <Tabs
                  size="sm"
                  items={PERSON_TYPE_OPTIONS.map((opt) => ({
                    value: opt.key,
                    label: _t(opt.title),
                    icon: <Icon name={opt.icon} />,
                  }))}
                  value={beneficiary.person_type}
                  onChange={(key) =>
                    onUpdate({
                      person_type: key,
                      document_type: key === "company" ? "cif" : "",
                      document_number: "",
                      first_name: "",
                      last_name: "",
                      date_of_birth: "",
                    })
                  }
                />
              )}
              {totalCount > 1 && index > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  iconStart={<Icon name="trash" />}
                  aria-label={_t("delete")}
                  onClick={onDelete}
                />
              )}
            </div>
          )}
          {index === 0 && showSaveToWorkspace && (
            <Button
              variant="link"
              size="sm"
              onClick={onSaveToWorkspace}
              disabled={savingToWorkspace}
            >
              {savingToWorkspace ? _t("saving") : _t("saveToWorkspace")}
            </Button>
          )}
        </div>
      </div>

      {/* Main fields (doc type/number, names) */}
      <div className="form-wrapper">
        {mainFields.map((field) => {
          const isIndividualNameField =
            field.name === "last_name" ||
            (field.name === "first_name" &&
              (insuranceCovered === "myself" ||
                beneficiary.person_type !== "company"));
          return (
            <div
              className={`main-form-group ${field?.class || ""}`}
              key={field.name}
            >
              {field.type === "select" ? (
                <FormSelect
                  id={`beneficiary-${index}-${field.name}`}
                  label={
                    <>
                      {_t(field.label)}
                      {field.required !== false && (
                        <span className="required-star"> *</span>
                      )}
                    </>
                  }
                  error={showErrors && errors[field.name]}
                  name={field.name}
                  value={beneficiary[field.name]}
                  onChange={(e) => onUpdate({ [field.name]: e.target.value })}
                  options={field.options.map((option) => ({
                    value: option.value,
                    label: _t(option.label),
                  }))}
                />
              ) : field.type === "date" ? (
                <>
                  <div
                    className={`form-group ${showErrors && errors[field.name] ? "form-group-error" : ""}`}
                  >
                    <label>
                      {_t(field.label)}
                      {field.required !== false && (
                        <span className="required-star"> *</span>
                      )}
                    </label>
                    <StyledDatePicker
                      value={beneficiary[field.name]}
                      onChange={(value) => onUpdate({ [field.name]: value })}
                      minDate={new Date("1900-01-01")}
                      maxDate={new Date()}
                      openToDate={
                        displayDateToDate(beneficiary[field.name]) ||
                        new Date(new Date().getFullYear() - 25, 0, 1)
                      }
                      hasError={showErrors && !!errors[field.name]}
                    />
                  </div>
                  {showErrors && errors[field.name] && (
                    <p className="error-message">{errors[field.name]}</p>
                  )}
                </>
              ) : (
                <TextField
                  id={`beneficiary-${index}-${field.name}`}
                  label={
                    <>
                      {_t(field.label)}
                      {field.required !== false && (
                        <span className="required-star"> *</span>
                      )}
                    </>
                  }
                  error={showErrors && errors[field.name]}
                  type="text"
                  name={field.name}
                  placeholder={_t(field.placeholder)}
                  value={beneficiary[field.name]}
                  onChange={(e) => onUpdate({ [field.name]: e.target.value })}
                  onBeforeInput={
                    isIndividualNameField
                      ? (e) => {
                          const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                          if (!isValidChar) e.preventDefault();
                        }
                      : undefined
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Address */}
      <AddressField
        _t={_t}
        insuranceDetails={beneficiary}
        setInsuranceDetails={setBeneficiary}
        isExpanded={isAddrExpanded}
        setIsExpanded={setIsAddrExpanded}
        showErrors={showErrors}
        errors={errors}
      />

      {/* Contact fields (email / phone) — only for someoneElse */}
      {contactFields.length > 0 && (
        <div className="form-wrapper">
          {contactFields.map((field) => (
            <div className="main-form-group" key={field.name}>
              {field.name === "phone_number" ? (
                <>
                  <div
                    className={`form-group ${
                      showErrors && errors[field.name] ? "form-group-error" : ""
                    }`}
                  >
                    <label>{_t(field.label)}</label>
                    <PhoneInput
                      className="phone_input_info"
                      international
                      country="es"
                      value={beneficiary.phone_number_full}
                      onChange={(value, country) => {
                        const dialCode = country?.dialCode || "";
                        const numberWithoutCode = value?.startsWith(dialCode)
                          ? value.slice(dialCode.length)
                          : value || "";
                        onUpdate({
                          phone_number_full: value,
                          phone_number: numberWithoutCode,
                        });
                      }}
                    />
                  </div>
                  {showErrors && errors[field.name] && (
                    <p className="error-message">{errors[field.name]}</p>
                  )}
                </>
              ) : (
                <TextField
                  id={`beneficiary-${index}-${field.name}`}
                  label={_t(field.label)}
                  error={showErrors && errors[field.name]}
                  type="email"
                  name={field.name}
                  placeholder={_t(field.placeholder)}
                  value={beneficiary[field.name]}
                  onChange={(e) => onUpdate({ [field.name]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const PurchaseInsurancePopup = ({
  insuracePopupModal,
  setInsuracePopupModal,
  selectedTenants,
  selections = [],
  property,
  rent,
  insuranceCost,
  insuranceCoverageMonths,
  preFilledInsuranceDocument,
  usedFor,
  onSkorProtectSuccess,
  onInsuranceSendPaymentLinkSuccess,
  onInsuranceError,
  onInsuranceSuccess,
  paymentAlreadyCompleted = false,
  onPaidCompletionFailed,
}) => {
  const { _t } = useTranslator();
  const { user: landlordUser, loadUserTenant } = useContext(AuthContext);
  const isSkorProtect = usedFor === "skorProtect";
  const selectedCoverageMonths = Number(insuranceCoverageMonths) || 12;
  const selectedCoverageLabel = String(_t("twelveMonthCoverage")).replace(
    /\b12\b/,
    String(selectedCoverageMonths)
  );
  const [showInsuranceReviewModal, setShowInsuranceReviewModal] =
    useState(false);

  const [redirectLoading, setRedirectLoading] = useState(false);
  const [insuranceCheckLoading, setInsuranceCheckLoading] = useState(false);
  // Completing an already paid insurance (not a payment link).
  const isPaidCompletionLoading =
    paymentAlreadyCompleted && !isSkorProtect && insuranceCheckLoading;
  const [sendPaymentLinkLoading, setSendPaymentLinkLoading] = useState(false);
  const [documentUploadLoading, setDocumentUploadLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [showPaymentButton, setShowPaymentButton] = useState(false);
  const [sendLinkError, setSendLinkError] = useState(false);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [validatedBeneficiaryIds, setValidatedBeneficiaryIds] = useState([]);
  const [savingBeneficiaryToWorkspace, setSavingBeneficiaryToWorkspace] =
    useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState(() => ({
    ...INITIAL_INSURANCE_DETAILS,
    gran_tenedor:
      landlordUser?.current_workspace?.beneficiary_details?.[0]
        ?.gran_tenedor === true,
    insurance_covered:
      landlordUser?.current_workspace?.beneficiary_details?.[0]?.person_type ===
      "company"
        ? "someoneElse"
        : INITIAL_INSURANCE_DETAILS.insurance_covered,
  }));
  const [beneficiaries, setBeneficiaries] = useState(() => [
    createBeneficiary(
      beneficiaryPrefillFromWorkspace(
        landlordUser?.current_workspace?.beneficiary_details?.[0]
      )
    ),
  ]);
  const [addrExpandedIds, setAddrExpandedIds] = useState({});
  const workspaceBeneficiarySnapshot = useRef(
    workspaceBeneficiaryPayload(
      landlordUser?.current_workspace?.beneficiary_details?.[0]
    )
  );

  useEffect(() => {
    if (!insuracePopupModal) return;
    setAddrExpandedIds({});
    setBeneficiaries((prev) => {
      const seenIds = new Set();
      let repairedDuplicateId = false;
      const next = prev.map((beneficiary) => {
        if (!seenIds.has(beneficiary.id)) {
          seenIds.add(beneficiary.id);
          return beneficiary;
        }
        repairedDuplicateId = true;
        const id = createBeneficiaryId();
        seenIds.add(id);
        return { ...beneficiary, id };
      });
      return repairedDuplicateId ? next : prev;
    });
  }, [insuracePopupModal]);

  useEffect(() => {
    if (insuracePopupModal) {
      setValidationAttempted(false);
      setValidatedBeneficiaryIds([]);
      workspaceBeneficiarySnapshot.current = workspaceBeneficiaryPayload(
        landlordUser?.current_workspace?.beneficiary_details?.[0]
      );
    }
  }, [insuracePopupModal, landlordUser]);

  const handleClose = () => {
    setValidationAttempted(false);
    setValidatedBeneficiaryIds([]);
    setInsuracePopupModal(false);
  };

  const updateBeneficiary = (beneficiaryIndex, updates) => {
    if (typeof updates !== "function" && updates.email !== undefined) {
      setSendLinkError(false);
    }
    setBeneficiaries((prev) =>
      prev.map((beneficiary, index) => {
        if (index !== beneficiaryIndex) return beneficiary;
        const resolvedUpdates =
          typeof updates === "function" ? updates(beneficiary) : updates;
        return { ...beneficiary, ...resolvedUpdates };
      })
    );
  };
  const deleteBeneficiary = (beneficiaryIndex) =>
    setBeneficiaries((prev) =>
      prev.filter((_, index) => index !== beneficiaryIndex)
    );
  const addBeneficiary = () =>
    setBeneficiaries((prev) => [...prev, createBeneficiary()]);

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // ── Prefill beneficiary from saved workspace defaults on open ──────────
  useEffect(() => {
    if (!insuracePopupModal) return;
    const savedBeneficiary =
      landlordUser?.current_workspace?.beneficiary_details?.[0];
    if (!savedBeneficiary) return;

    setInsuranceDetails((prev) => ({
      ...prev,
      gran_tenedor: savedBeneficiary.gran_tenedor === true,
    }));

    let wasPristine = false;
    setBeneficiaries((prev) => {
      wasPristine =
        prev.length === 1 &&
        !prev[0].document_number &&
        !prev[0].first_name &&
        !prev[0].province;
      if (!wasPristine) return prev;
      return [
        createBeneficiary(beneficiaryPrefillFromWorkspace(savedBeneficiary)),
      ];
    });

    if (wasPristine && savedBeneficiary.person_type === "company") {
      setInsuranceDetails((prev) => ({
        ...prev,
        insurance_covered: "someoneElse",
      }));
    }
  }, [insuracePopupModal, landlordUser]);

  const {
    initiateInsuracePayment,
    uploadFileData,
    checkForInsuranceEligibility,
    updateWorkspaceBeneficiary,
  } = useContext(LandLordContext);

  // ── File validation ────────────────────────
  const validateFiles = async (newFiles) => {
    const validated = [];
    const invalids = [];

    for (const file of newFiles) {
      const id = `${file.name}-${file.lastModified}`;

      if (!ALLOWED_TYPES.includes(file.type)) {
        invalids.push({
          id,
          name: file.name,
          reason: _t("fileTypeNotAllowed"),
        });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        invalids.push({
          id,
          name: file.name,
          reason: _t("fileUploadedExceedsTwoPointFiveMbFileLimit"),
        });
        continue;
      }

      try {
        if (file.type === "application/pdf") {
          const arrayBuffer = await file.arrayBuffer();
          await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        } else {
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = () => reject(new Error("Corrupted image"));
            img.src = URL.createObjectURL(file);
          });
        }
        validated.push({ id, file, name: file.name, size: file.size });
      } catch (err) {
        const reason = err?.message?.toLowerCase().includes("password")
          ? _t("isPasswordProtectedPDF")
          : _t("isCorruptedOrUnreadableFile");
        invalids.push({ id, name: file.name, reason });
      }
    }

    if (invalids.length) setInvalidFiles((prev) => [...prev, ...invalids]);
    return validated;
  };

  const addFiles = async (rawFiles) => {
    if (documentFiles.length > 0) {
      toast.error(_t("onlyOneFileAllowed"));
      return;
    }
    const valid = await validateFiles([rawFiles[0]]);
    if (valid.length) setDocumentFiles(valid);
  };

  // ── Drag & drop ────────────────────────────
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files?.length) await addFiles(e.dataTransfer.files);
  };
  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  // ── Form helpers ───────────────────────────
  const isValid = () => {
    const { insurance_covered, lease_start_date, policy_start_date } =
      insuranceDetails;
    if (!insurance_covered || !lease_start_date || !policy_start_date)
      return false;
    const leaseDate = displayDateToDate(lease_start_date);
    const policyDate = displayDateToDate(policy_start_date);
    if (!leaseDate || !policyDate || policyDate < leaseDate) return false;
    return beneficiaries.every((b) => {
      const hasAddress =
        b.province &&
        b.city &&
        b.street_name &&
        b.street_type &&
        b.street_number &&
        b.postcode;
      if (!hasAddress) return false;

      if (insurance_covered === "myself") {
        return (
          !!b.document_type &&
          !!b.document_number &&
          !!b.first_name &&
          !!b.last_name &&
          isValidDateOfBirth(b.date_of_birth)
        );
      }

      if (b.person_type === "company") {
        return !!b.document_number && !!b.first_name;
      }

      return (
        !!b.document_type &&
        !!b.document_number &&
        !!b.first_name &&
        !!b.last_name &&
        isValidDateOfBirth(b.date_of_birth)
      );
    });
  };

  const selectedTenantDocumentNumbers = useMemo(
    () =>
      new Set(
        getSelectedTenantRecords(selectedTenants)
          .flatMap((tenant) => [tenant?.dni_nie, tenant?.passport_id])
          .map(normalizeDocumentNumber)
          .filter(Boolean)
      ),
    [selectedTenants]
  );

  const beneficiaryErrors = useMemo(
    () =>
      beneficiaries.map((beneficiary) => {
        const errors = {};
        const required = (name, key) => {
          if (!String(beneficiary[name] ?? "").trim()) errors[name] = _t(key);
        };

        required("province", "provinceIsRequired");
        required("city", "municipalityIsRequired");
        required("street_type", "streetTypeIsRequired");
        required("street_name", "streetNameIsRequired");
        required("street_number", "streetNumberIsRequired");
        required("postcode", "postcodeIsRequired");
        const selectedProvince = provincesData.find(
          (province) => province.Provincia === beneficiary.province
        );
        if (beneficiary.province && !selectedProvince) {
          errors.province = _t("invalidProvince");
        }
        if (
          beneficiary.city &&
          (!selectedProvince ||
            !selectedProvince.municipios.includes(beneficiary.city))
        ) {
          errors.city = _t("invalidMunicipality");
        }
        if (
          beneficiary.street_type &&
          !Object.values(streetTypeCodes).includes(beneficiary.street_type)
        ) {
          errors.street_type = _t("invalidStreetType");
        }
        if (
          beneficiary.postcode &&
          !/^\d{5}$/.test(String(beneficiary.postcode).trim())
        ) {
          errors.postcode = _t("postalCodeMustBeFiveDigits");
        }

        if (beneficiary.person_type === "company") {
          required("document_number", "documentNumberIsRequired");
          required("first_name", "companyNameIsRequired");
        } else {
          required("document_type", "documentTypeIsRequired");
          required("document_number", "documentNumberIsRequired");
          required("first_name", "firstNameIsRequired");
          required("last_name", "lastNameIsRequired");
          if (!beneficiary.date_of_birth) {
            errors.date_of_birth = _t("dateOfBirthIsRequired");
          } else if (!isValidDateOfBirth(beneficiary.date_of_birth)) {
            errors.date_of_birth = _t("invalidDateOfBirth");
          }
        }
        const normalizedDocumentNumber = normalizeDocumentNumber(
          beneficiary.document_number
        );
        if (
          beneficiary.document_number &&
          beneficiary.document_number.trim().length !== 9
        ) {
          errors.document_number = _t("documentNumberMustBeNineCharacters");
        }
        if (
          normalizedDocumentNumber &&
          selectedTenantDocumentNumbers.has(normalizedDocumentNumber)
        ) {
          errors.document_number = _t("policyholderDocumentMustNotMatchTenant");
        }
        const email = beneficiary.email?.trim();
        if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
          errors.email = _t("invalidEmailFormat");
        }
        const phoneNumber = String(
          beneficiary.phone_number_full || beneficiary.phone_number || ""
        ).replace(/\D/g, "");
        if (phoneNumber && phoneNumber.length <= 5) {
          errors.phone_number = _t("phoneNumberMustBeAtLeastSixDigits");
        }
        return errors;
      }),
    [beneficiaries, selectedTenantDocumentNumbers, _t]
  );

  const firstBeneficiaryChangedFromWorkspace =
    beneficiaries.length > 0 &&
    !beneficiaryMatchesWorkspace(
      beneficiaries[0],
      workspaceBeneficiarySnapshot.current,
      insuranceDetails.gran_tenedor
    );

  const handleSaveBeneficiaryToWorkspace = async () => {
    const beneficiary = beneficiaries[0];
    if (!beneficiary || Object.keys(beneficiaryErrors[0] || {}).length > 0) {
      setValidationAttempted(true);
      if (beneficiary) setValidatedBeneficiaryIds([beneficiary.id]);
      return;
    }

    const payload = workspaceBeneficiaryPayload(
      beneficiary,
      insuranceDetails.gran_tenedor
    );
    try {
      setSavingBeneficiaryToWorkspace(true);
      await updateWorkspaceBeneficiary({ beneficiaries: [payload] });
      workspaceBeneficiarySnapshot.current = payload;
      await loadUserTenant("landlord");
      toast.success(_t("beneficiaryDetailsSaved"));
    } catch (err) {
      console.error("Error saving beneficiary to workspace:", err);
      toast.error(_t("errorWhileSavingBeneficiaryDetails"));
    } finally {
      setSavingBeneficiaryToWorkspace(false);
    }
  };

  const selectedTenantErrors = useMemo(() => {
    const tenants = getSelectedTenantRecords(selectedTenants);

    return tenants.filter(Boolean).flatMap((tenant) => {
      const kyc = tenant?.onfido_user_kyc?.onfido_doc_data || {};
      const identity = getIdentityDetails(
        tenant,
        tenant?.onfido_user_kyc,
        true
      );
      const missing = [];
      if (!identity?.type) missing.push(_t("documentType"));
      if (!identity?.value) missing.push(_t("documentNumber"));
      if (!kyc?.first_name && !tenant?.fname && !tenant?.first_name)
        missing.push(_t("firstName"));
      if (!kyc?.last_name && !tenant?.lname && !tenant?.last_name)
        missing.push(_t("lastName"));
      if (!tenant?.dob && !tenant?.date_of_birth)
        missing.push(_t("dateofbirth"));
      if (!missing.length) return [];

      const name = getUserDisplayName(tenant, tenant?.email || _t("tenant"));
      return [
        `${name}: ${_t("missingTenantDetailsForInsurance")} ${missing.join(", ")}`,
      ];
    });
  }, [selectedTenants, _t]);

  const propertyErrors = useMemo(() => {
    const errors = [];
    if (!haveValue(rent) || Number(rent) < 0) errors.push(_t("rentIsRequired"));
    if (!property?.province) errors.push(_t("provinceIsRequired"));
    if (!property?.muncipality) errors.push(_t("municipalityIsRequired"));
    if (!property?.postcode) errors.push(_t("postcodeIsRequired"));
    if (!property?.street) errors.push(_t("streetNameIsRequired"));
    if (!property?.street_number) errors.push(_t("streetNumberIsRequired"));
    if (!selectedTenants?.length) errors.push(_t("atLeastOneTenantIsRequired"));
    return [...errors, ...selectedTenantErrors];
  }, [property, rent, selectedTenants, selectedTenantErrors, _t]);

  const validateBeforeInsuranceCall = () => {
    setValidationAttempted(true);
    setValidatedBeneficiaryIds(
      beneficiaries.map((beneficiary) => beneficiary.id)
    );
    const hasBeneficiaryErrors = beneficiaryErrors.some(
      (errors) => Object.keys(errors).length > 0
    );
    return (
      isValid() &&
      !hasBeneficiaryErrors &&
      propertyErrors.length === 0 &&
      documentFiles.length > 0
    );
  };

  const policyDateError = useMemo(() => {
    if (!insuranceDetails.policy_start_date)
      return _t("policyStartDateIsRequired");
    const leaseDate = displayDateToDate(insuranceDetails.lease_start_date);
    const policyDate = displayDateToDate(insuranceDetails.policy_start_date);
    if (!policyDate) return _t("invalidPolicyStartDate");
    if (leaseDate && policyDate < leaseDate)
      return _t("policyStartDateMustNotBeBeforeLeaseStartDate");
    return "";
  }, [
    insuranceDetails.lease_start_date,
    insuranceDetails.policy_start_date,
    _t,
  ]);

  // ── Actions ────────────────────────────────
  const handleUploadFiles = async (fileType = "rental_contract") => {
    try {
      setDocumentUploadLoading(true);
      const formData = new FormData();
      documentFiles.forEach((f) => formData.append("landlordDocs", f.file));
      formData.append("fileType", fileType);
      formData.append("propertyId", property?._id);
      await uploadFileData(formData);
    } catch (err) {
      console.error("Error uploading files:", err);
    } finally {
      setDocumentUploadLoading(false);
    }
  };

  const handleMakePayment = async () => {
    try {
      setRedirectLoading(true);
      const data = await initiateInsuracePayment(property?._id, {
        report_rent: rent,
        selections,
      });
      if (data?.status === "success") {
        window.location.replace(data?.paymentUrl);
      }
    } catch {
      console.log("Failed to redirect to payment page");
      setRedirectLoading(false);
    }
  };

  const checkForInsurance = async ({ sendPaymentLinkToBeneficiary }) => {
    if (!validateBeforeInsuranceCall()) return;
    console.log("Form JSON:", { insuranceDetails, beneficiaries });

    // Paid insurance: MDP calls 4 and 5 run in this request; the overlay
    // shows "Processing insurance" until it returns.
    const isPaidCompletion =
      paymentAlreadyCompleted && !sendPaymentLinkToBeneficiary;
    // For now a failed paid completion just closes everything.
    const closeFailedPaidCompletion = () => {
      setInsuracePopupModal(false);
      onPaidCompletionFailed?.();
    };

    try {
      if (sendPaymentLinkToBeneficiary) {
        setSendPaymentLinkLoading(true);
      } else {
        setInsuranceCheckLoading(true);
      }
      await handleUploadFiles();

      const payload = {
        ...insuranceDetails,
        propertyId: property?._id,
        property_rent: rent,
        selections,
        // Backend structure: array of beneficiary objects
        beneficiaries: beneficiaries.map(
          ({ id, phone_number_full, ...rest }) => rest
        ),
        ...(usedFor ? { usedFor } : {}),
        ...(sendPaymentLinkToBeneficiary
          ? { sendPaymentLink: sendPaymentLinkToBeneficiary }
          : {}),
      };

      const saveRes = await checkForInsuranceEligibility(payload);

      if (!saveRes?.success) {
        if (isPaidCompletion) {
          closeFailedPaidCompletion();
          return;
        }
        if (onInsuranceError) {
          setInsuracePopupModal(false);
          onInsuranceError({
            errorCode: saveRes?.errorCode ?? null,
            retry: () => checkForInsurance({ sendPaymentLinkToBeneficiary }),
          });
          return;
        }
        toast.error(saveRes?.message);
        setShowInsuranceReviewModal(true);
        return;
      }

      const insuranceResult = saveRes?.success_res;
      const insuranceMessage =
        typeof insuranceResult === "string"
          ? insuranceResult
          : insuranceResult?.message;
      const isInsuranceAccepted =
        insuranceMessage === constants.ACCEPTED_INSURANCE_MSG ||
        insuranceResult?.manual_review === true ||
        insuranceResult?.being_issued === true;

      if (isInsuranceAccepted) {
        // What the landlord just submitted, for the "Being issued" popup.
        await onInsuranceSuccess?.({
          beingIssued: insuranceResult?.being_issued === true,
          submittedDetails: {
            beneficiaries: beneficiaries.map((beneficiary) => ({
              name: `${beneficiary?.first_name ?? ""} ${
                beneficiary?.last_name ?? ""
              }`.trim(),
              documentNumber: beneficiary?.document_number ?? "",
            })),
            policyStartDate: insuranceDetails?.policy_start_date ?? null,
            contractFileName: documentFiles?.[0]?.file?.name ?? null,
          },
        });
        if (isSkorProtect) {
          await onSkorProtectSuccess?.();
        } else if (sendPaymentLinkToBeneficiary) {
          await onInsuranceSendPaymentLinkSuccess?.();
        } else if (paymentAlreadyCompleted) {
          setInsuracePopupModal(false);
        } else {
          handleMakePayment();
          toast.success(
            _t("youAreEligibleForInsuranceYouCanContinueWithPayment")
          );
        }
      } else if (isPaidCompletion) {
        closeFailedPaidCompletion();
      } else {
        toast.error(insuranceMessage);
        setShowInsuranceReviewModal(true);
      }
    } catch {
      console.log("Failed to check insurance eligibility");
      if (isPaidCompletion) closeFailedPaidCompletion();
    } finally {
      if (sendPaymentLinkToBeneficiary) {
        setSendPaymentLinkLoading(false);
      } else {
        setInsuranceCheckLoading(false);
      }
    }
  };

  const handleSendPaymentLink = async () => {
    if (!validateBeforeInsuranceCall()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const filledEmails = beneficiaries
      .map((b) => b.email?.trim())
      .filter(Boolean);
    if (!filledEmails.length) {
      setSendLinkError(_t("noEmailIsEnteredInBeneficiary"));
      return;
    }
    const hasInvalid = filledEmails.some((e) => !emailRegex.test(e));
    if (hasInvalid) {
      setSendLinkError("Please enter a valid email in beneficiary");
      return;
    }
    setSendLinkError(null);
    await checkForInsurance({ sendPaymentLinkToBeneficiary: true });
  };

  // ── Derived values ─────────────────────────
  const isSubmitDisabled = redirectLoading;

  const handleCoveredClick = (toggleKey) => {
    if (toggleKey === insuranceDetails.insurance_covered) return;
    setInsuranceDetails((prev) => ({ ...prev, insurance_covered: toggleKey }));
    setBeneficiaries([
      createBeneficiary(
        beneficiaryPrefillFromWorkspace(
          landlordUser?.current_workspace?.beneficiary_details?.[0]
        )
      ),
    ]);
    setAddrExpandedIds({});
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <>
      {!showInsuranceReviewModal ? (
        <Modal
          show={insuracePopupModal}
          className="metermodal wrapper-modal modal-xl custom-modal-popup purchase-insurance-modal"
          centered
          onHide={handleClose}
        >
          <Modal.Header>
            <h2 className="modal-title-text">
              {isSkorProtect
                ? _t("uploadContract")
                : _t("purchaseInsuranceHeading")}
            </h2>
            <div className="cursor-pointer" onClick={handleClose}>
              <img
                src={Cancelimg}
                alt="icon"
                className="canceicon topRightIcon"
              />
            </div>
          </Modal.Header>

          <Modal.Body>
            <div className="bodyContentset">
              <form onSubmit={(e) => e.preventDefault()}>
                {/* ── Insurance summary banner ── */}
                {!isSkorProtect && (
                  <div className="purchase-insurance-details">
                    <div className="purchase-insurance-details-info">
                      <h1>
                        {selectedCoverageLabel}
                        {" - "}
                        {insuranceCost != null ? `€${insuranceCost}` : "-"}
                        {selectedCoverageMonths === 12 ? `/${_t("year")}` : ""}
                      </h1>
                    </div>
                    {preFilledInsuranceDocument && (
                      <a
                        href={`${preFilledInsuranceDocument}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="purchase-insurance-coverage-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DownloadIcon />
                        {_t("insuranceCoverage")}
                      </a>
                    )}
                  </div>
                )}

                {/* ── Fields + overlay wrapper ── */}
                <div style={{ position: "relative" }}>
                  <div
                    style={
                      insuranceCheckLoading || sendPaymentLinkLoading
                        ? { opacity: 0.25, pointerEvents: "none" }
                        : {}
                    }
                  >
                    {/* ── Who is covered ── */}
                    <div className="purchase-insurance-details-wrapper d-flex flex-column bigform">
                      <h5>{_t("whoWillBeCoveredByThisInsurance")}</h5>
                      <Tabs
                        block
                        size="lg"
                        label={_t("whoWillBeCoveredByThisInsurance")}
                        items={INSURANCE_COVERED_OPTIONS.map((toggle) => ({
                          value: toggle.key,
                          label: _t(toggle.title),
                          icon: <Icon name={toggle.icon} />,
                        }))}
                        value={insuranceDetails.insurance_covered}
                        onChange={handleCoveredClick}
                      />

                      {/* ── Beneficiary cards ── */}
                      {beneficiaries.map((beneficiary, index) => (
                        <BeneficiaryCard
                          key={beneficiary.id}
                          hideCard={
                            insuranceCheckLoading || sendPaymentLinkLoading
                          }
                          beneficiary={beneficiary}
                          index={index}
                          totalCount={beneficiaries.length}
                          onUpdate={(updates) =>
                            updateBeneficiary(index, updates)
                          }
                          onDelete={() => deleteBeneficiary(index)}
                          insuranceCovered={insuranceDetails.insurance_covered}
                          isAddrExpanded={!!addrExpandedIds[beneficiary.id]}
                          setIsAddrExpanded={(val) =>
                            setAddrExpandedIds((prev) => ({
                              ...prev,
                              [beneficiary.id]: val,
                            }))
                          }
                          showErrors={
                            validationAttempted &&
                            validatedBeneficiaryIds.includes(beneficiary.id)
                          }
                          errors={beneficiaryErrors[index]}
                          showSaveToWorkspace={
                            index === 0 && firstBeneficiaryChangedFromWorkspace
                          }
                          savingToWorkspace={savingBeneficiaryToWorkspace}
                          onSaveToWorkspace={handleSaveBeneficiaryToWorkspace}
                          _t={_t}
                        />
                      ))}

                      {/* ── Add beneficiary button ── */}
                      <Button
                        variant="secondary"
                        block
                        iconStart={<Icon name="plus" />}
                        onClick={addBeneficiary}
                      >
                        {_t("addAnotherBeneficiary")}
                      </Button>

                      {/* ── Gran tenedor ── */}
                      <div
                        className="gran-tenedor-field d-flex align-items-center justify-content-between cursor-pointer"
                        onClick={() =>
                          setInsuranceDetails((prev) => ({
                            ...prev,
                            gran_tenedor: !prev.gran_tenedor,
                          }))
                        }
                      >
                        <div className="d-flex flex-column">
                          <span className="gran-tenedor-title">
                            {_t("granTenedor")}
                          </span>
                          <span className="gran-tenedor-subtitle">
                            {_t("granTenedorSubtitle")}
                          </span>
                        </div>
                        <div className="d-flex align-items-center custom-gap-2">
                          <span className="gran-tenedor-value-label">
                            {insuranceDetails.gran_tenedor
                              ? _t("yes")
                              : _t("no")}
                          </span>
                          <div
                            className={`gran-tenedor-toggle ${insuranceDetails.gran_tenedor ? "active" : ""}`}
                          >
                            <div className="gran-tenedor-thumb" />
                          </div>
                        </div>
                      </div>

                      {/* ── Dates ── */}
                      {insuranceDetails.insurance_covered !== "" && (
                        <div className="form-wrapper mt-0">
                          {[
                            {
                              name: "lease_start_date",
                              label: "leaseStartDate",
                            },
                            {
                              name: "policy_start_date",
                              label: "policyStartDate",
                            },
                          ].map(({ name, label }) => (
                            <div className="main-form-group" key={name}>
                              <div
                                className={`form-group ${
                                  validationAttempted &&
                                  (name === "policy_start_date"
                                    ? !!policyDateError
                                    : !insuranceDetails[name])
                                    ? "form-group-error"
                                    : ""
                                }`}
                              >
                                <label>
                                  {_t(label)}{" "}
                                  <span className="required-star">*</span>
                                </label>
                                <StyledDatePicker
                                  value={insuranceDetails[name]}
                                  minDate={
                                    name === "policy_start_date" &&
                                    insuranceDetails.lease_start_date
                                      ? displayDateToDate(
                                          insuranceDetails.lease_start_date
                                        )
                                      : new Date()
                                  }
                                  openToDate={
                                    displayDateToDate(insuranceDetails[name]) ||
                                    new Date()
                                  }
                                  onChange={(value) =>
                                    setInsuranceDetails((prev) => ({
                                      ...prev,
                                      [name]: value,
                                    }))
                                  }
                                  hasError={
                                    validationAttempted &&
                                    (name === "policy_start_date"
                                      ? !!policyDateError
                                      : !insuranceDetails[name])
                                  }
                                />
                              </div>
                              {validationAttempted &&
                                (name === "policy_start_date"
                                  ? !!policyDateError
                                  : !insuranceDetails[name]) && (
                                  <p className="error-message">
                                    {name === "policy_start_date"
                                      ? policyDateError
                                      : _t("leaseStartDateIsRequired")}
                                  </p>
                                )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* end purchase-insurance-details-wrapper */}

                    {/* ── File upload area — hidden while checking ── */}
                    {!insuranceCheckLoading &&
                      !sendPaymentLinkLoading &&
                      (documentUploadLoading ? (
                        <div role="status" aria-label={_t("loading")}>
                          <Skeleton height={244} radius="lg" />
                        </div>
                      ) : (
                        <div
                          className={`doc-listing doc-listing2 doc-listing3 h-100 d-flex align-items-center justify-content-center text-center ${
                            !documentFiles.length && !invalidFiles.length
                              ? "cursor-pointer"
                              : ""
                          } ${
                            validationAttempted && documentFiles.length === 0
                              ? "input-error"
                              : ""
                          } purchase-insurance-dropzone ${
                            isDragging ? "is-dragging" : ""
                          }`}
                          onClick={() => {
                            if (!documentFiles.length && !invalidFiles.length)
                              fileInputRef.current.click();
                          }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragEnter}
                          onDragLeave={handleDragLeave}
                        >
                          <FileInput
                            ref={fileInputRef}
                            onChange={(e) => {
                              if (e.target.files?.length)
                                addFiles(e.target.files);
                            }}
                            style={{ display: "none" }}
                            accept="application/pdf,image/jpeg,image/png,image/jpg"
                          />

                          {documentFiles.length > 0 ||
                          invalidFiles.length > 0 ? (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                              }}
                            >
                              <div className="uploaded-list">
                                {/* Invalid files */}
                                {invalidFiles.map((file) => (
                                  <div
                                    key={file.id}
                                    className="d-flex uploaded-files-view uploaded-files-view--invalid align-items-center justify-content-between flex-wrap"
                                  >
                                    <div className="d-flex flex-column justify-content-between">
                                      <div className="uploaded-files-view-name">
                                        <img
                                          src="/images/docIconGreen.svg"
                                          alt=""
                                        />
                                        <div className="upload-file-name">
                                          <h1
                                            className="mb-0 text-truncate"
                                            style={{ maxWidth: "250px" }}
                                          >
                                            {file.name}
                                          </h1>
                                        </div>
                                      </div>
                                      <div className="corrupted-upload-doucmentFiles purchase-insurance-invalid-reason">
                                        <Icon name="warning" />
                                        <span>{file.reason}</span>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      iconOnly
                                      iconStart={<Icon name="trash" />}
                                      aria-label={_t("removeFile")}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInvalidFiles((prev) =>
                                          prev.filter((f) => f.id !== file.id)
                                        );
                                      }}
                                    />
                                  </div>
                                ))}

                                {/* Valid files */}
                                {documentFiles.map((fileObj) => (
                                  <div
                                    key={fileObj.id}
                                    className="d-flex uploaded-files-view align-items-center justify-content-between flex-wrap"
                                  >
                                    <div className="uploaded-files-view-name">
                                      <img
                                        src="/images/docIconGreen.svg"
                                        alt=""
                                      />
                                      <div className="upload-file-name">
                                        <h1
                                          className="text-truncate"
                                          style={{ maxWidth: "250px" }}
                                        >
                                          {fileObj.name}
                                        </h1>
                                        <span>{formatSize(fileObj.size)}</span>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      iconOnly
                                      iconStart={<Icon name="trash" />}
                                      aria-label={_t("removeFile")}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDocumentFiles((prev) =>
                                          prev.filter(
                                            (f) => f.id !== fileObj.id
                                          )
                                        );
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="docUpload">
                              <div className="Uploadimgdiv">
                                <img src={Uploadimg} alt="upload-img" />
                              </div>
                              <p className="drag-drop-content pt-3">
                                {_t("dragAndDropYourRentalContract")}
                              </p>
                              <p className="spacepx">{_t("or")}</p>
                              <Button variant="primary">
                                {_t("selectFromFiles")}
                              </Button>
                              <p className="fileSizeText">
                                {_t("ensureTheSizeOfFileMustBeLessThan4Mb")}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    {validationAttempted && documentFiles.length === 0 && (
                      <p className="error-message">
                        {_t("rentalContractIsRequired")}
                      </p>
                    )}
                  </div>
                  {/* end faded fields div */}

                  {/* ── Checking insurability overlay ── */}
                  {(insuranceCheckLoading || sendPaymentLinkLoading) && (
                    <div className="insurance-checking-overlay">
                      <div className="insurance-checking-content">
                        {isSkorProtect ? (
                          <img
                            src={SkorProtectShield}
                            alt=""
                            className="skor-protect-checking-logo"
                          />
                        ) : (
                          <div className="insurance-checking-icon-circle">
                            <OutlineShieldIcon />
                          </div>
                        )}
                        {/* Paid insurance: no eligibility check, the policy
                            is being issued. */}
                        <h3 className="insurance-checking-title">
                          {isPaidCompletionLoading
                            ? _t("processingInsurance")
                            : _t("checkingInsurability")}
                        </h3>
                        <p className="insurance-checking-subtitle">
                          {isPaidCompletionLoading
                            ? _t("processingTheInsurance")
                            : _t("checkingInsurabilitySubtitle")}
                        </p>
                        <div
                          className={`insurance-checking-dots${
                            isSkorProtect ? " skor-protect-dots" : ""
                          }`}
                        >
                          <span />
                          <span />
                          <span />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* end relative wrapper */}

                {/* ── Property errors ── */}
                {validationAttempted && propertyErrors.length > 0 && (
                  <Notice
                    tone="danger"
                    className="mt-3 mb-2"
                    title={_t("pleaseAddMissingDetailsForInsurance")}
                  >
                    <ul className="mb-0 ps-3">
                      {propertyErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </Notice>
                )}

                {/* ── Submit button ── */}
                <div className="row100 gridbar popup_btn2">
                  {!sendPaymentLinkLoading && (
                    <Button
                      variant="primary"
                      size="lg"
                      block
                      type="submit"
                      onClick={checkForInsurance}
                      disabled={isSubmitDisabled || insuranceCheckLoading}
                      loading={insuranceCheckLoading}
                    >
                      {isSkorProtect && insuranceCheckLoading
                        ? `${_t("uploadingContract")}...`
                        : isSkorProtect
                          ? _t("activateSkorProtect")
                          : redirectLoading
                            ? `${_t("redirecting")}...`
                            : insuranceCheckLoading
                              ? paymentAlreadyCompleted
                                ? `${_t("processingInsurance")}...`
                                : _t("checkingForInsurance")
                              : _t("checkForInsuranceEligibility")}
                    </Button>
                  )}

                  {/* Legacy payer link; never for a premium that is already paid. */}
                  {!insuranceCheckLoading &&
                    !paymentAlreadyCompleted &&
                    usedFor !== "skorProtect" &&
                    insuranceDetails.insurance_covered === "someoneElse" && (
                      <div className="send-payment-link-wrapper">
                        {!sendPaymentLinkLoading && (
                          <div className="send-payment-link-divider">
                            <span>{_t("or")}</span>
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="lg"
                          block
                          iconStart={<Icon name="send" />}
                          onClick={handleSendPaymentLink}
                          disabled={isSubmitDisabled}
                          loading={sendPaymentLinkLoading}
                        >
                          {sendPaymentLinkLoading
                            ? `${_t("checkingForInsuranceAndSendingPaymentLink")}...`
                            : _t("sendPaymentLink")}
                        </Button>
                        {sendLinkError && (
                          <p className="send-payment-link-error">
                            {sendLinkError}
                          </p>
                        )}
                        <p className="send-payment-link-note">
                          <img
                            src="/images/clock.svg"
                            alt="clock"
                            className="send-payment-link-clock-icon"
                          />
                          {_t("sendPaymentLinkNote")}
                        </p>
                      </div>
                    )}
                </div>
              </form>
            </div>
          </Modal.Body>
        </Modal>
      ) : (
        <InsuranceReviewModal
          show={showInsuranceReviewModal}
          onHide={() => setShowInsuranceReviewModal(false)}
          onGotIt={() => {
            setShowInsuranceReviewModal(false);
          }}
        />
      )}
    </>
  );
};

export default PurchaseInsurancePopup;
